import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";
import { ApiError, data, portal, setCsrf, type Edition, type Me } from "@aawaz/api-client";

interface Session {
  me: Me | undefined;
  loading: boolean;
  needsTenant: boolean;
  needsTotp: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  can: (perm: Perm) => boolean;
  /** The firm's product: "ca" (practice: clients, GST desk) and/or "accounting" (own books). */
  has: (edition: Edition) => boolean;
}

// Mirrors security/rbac.py PORTAL_POLICY; the server enforces it — this only hides controls.
export type Perm = "download" | "review" | "manage_clients" | "whatsapp" | "manage_connections" | "enable_write"
  | "manage_members" | "manage_api" | "audit" | "tenant_settings";
const POLICY: Record<Perm, Me["role"][]> = {
  download: ["owner", "admin", "staff"], review: ["owner", "admin", "staff"], manage_clients: ["owner", "admin"],
  whatsapp: ["owner", "admin", "staff"], manage_connections: ["owner", "admin"], enable_write: ["owner"],
  manage_members: ["owner", "admin"], manage_api: ["owner", "admin"], audit: ["owner", "admin"], tenant_settings: ["owner"],
};

const Ctx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["me"],
    queryFn: () => data<Me>(portal.GET("/portal/v1/me")),
    retry: false,
  });
  const code = q.error instanceof ApiError ? q.error.code : null;
  const value: Session = {
    me: q.data,
    loading: q.isLoading,
    needsTenant: code === "NO_ACTIVE_TENANT",
    needsTotp: code === "SECOND_FACTOR_REQUIRED",
    refresh: async () => { await qc.invalidateQueries(); },
    logout: async () => {
      await portal.POST("/portal/v1/auth/logout").catch(() => undefined);
      setCsrf(null);
      qc.clear();
      window.location.assign("/login");
    },
    can: (perm) => !!q.data && POLICY[perm].includes(q.data.role),
    has: (edition) => (q.data?.firm?.editions ?? ["ca"]).includes(edition),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error("SessionProvider missing");
  return s;
}
